
import React, { useEffect, useState } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { useAuth } from '../context/AuthContext';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { CheckCircle, XCircle, Loader2, Crown, Zap, Mail } from 'lucide-react';

import { BACKEND_URL } from '../config';

export default function PaymentStatusPage() {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const { token, refreshUser, setSession } = useAuth();
  const orderId = searchParams.get('order_id');

  const [status, setStatus] = useState('verifying');
  const [planDetails, setPlanDetails] = useState(null);
  const [isNewUser, setIsNewUser] = useState(false);
  const [onboardingCompleted, setOnboardingCompleted] = useState(true);

  useEffect(() => {
    if (!orderId) {
      setStatus('failed');
      return;
    }
    verifyPayment();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [orderId]);

  const verifyPayment = async () => {
    try {
      // Send token if available; backend also accepts unauthenticated calls
      // and resolves the user from the order_id mapping (guest checkout).
      const headers = token ? { Authorization: `Bearer ${token}` } : {};
      const response = await axios.post(
        `${BACKEND_URL}/api/payment/verify`,
        { order_id: orderId },
        { headers }
      );

      if (response.data.success) {
        const data = response.data.data || {};
        setPlanDetails(data);
        setIsNewUser(!!data.is_new_user);
        setOnboardingCompleted(data.onboarding_completed !== false);

        // For guest checkouts the backend issues a JWT — log them in.
        if (data.token && data.user) {
          setSession(data.token, data.user);
        } else if (token) {
          await refreshUser();
        }

        setStatus('success');
      } else {
        setStatus('failed');
      }
    } catch (error) {
      console.error('Verification error:', error);
      setStatus('failed');
    }
  };

  const planName = planDetails?.plan === 'premium' ? 'Premium' : 'Pro';
  const mocksPerMonth = planDetails?.plan === 'premium' ? 10 : 8;
  const PlanIcon = planDetails?.plan === 'premium' ? Crown : Zap;
  const planColor = planDetails?.plan === 'premium' ? 'text-amber-500' : 'text-blue-500';

  const continueLabel = !onboardingCompleted ? 'Pick your subjects' : 'Go to Dashboard';
  const continueTo = !onboardingCompleted ? '/onboarding' : '/dashboard';

  return (
    <div className="min-h-screen bg-gray-50 flex items-center justify-center p-4">
      <Card className="w-full max-w-lg text-center shadow-xl border-0">
        <CardContent className="pt-8 pb-10 px-8">

          {status === 'verifying' && (
            <div className="flex flex-col items-center" data-testid="payment-status-verifying">
              <Loader2 className="h-16 w-16 text-blue-500 animate-spin mb-4" />
              <h2 className="text-xl font-bold text-gray-800">Verifying your payment...</h2>
              <p className="text-gray-500 mt-2">Please wait, do not close this window.</p>
            </div>
          )}

          {status === 'success' && (
            <div className="flex flex-col items-center" data-testid="payment-status-success">
              <div className="w-20 h-20 bg-green-100 rounded-full flex items-center justify-center mb-5">
                <CheckCircle className="h-12 w-12 text-green-500" />
              </div>

              <h2 className="text-2xl font-bold text-gray-900 mb-1">
                Thanks for your payment!
              </h2>
              <p className="text-gray-500 mb-6">
                {isNewUser
                  ? "Your account is ready. Pick your subjects and start your first mock."
                  : "Your account has been successfully upgraded."}
              </p>

              {/* Plan Badge */}
              <div className="bg-gray-50 border border-gray-200 rounded-2xl px-6 py-5 mb-6 w-full">
                <div className="flex items-center justify-center gap-2 mb-3">
                  <PlanIcon className={`h-6 w-6 ${planColor}`} />
                  <span className={`text-xl font-bold ${planColor}`}>
                    You are now a {planName} user!
                  </span>
                </div>
                <div className="grid grid-cols-2 gap-3 text-sm">
                  <div className="bg-white rounded-lg p-3 border border-gray-100">
                    <p className="text-gray-500 text-xs mb-1">Mocks per subject</p>
                    <p className="font-bold text-gray-900">{mocksPerMonth} / month</p>
                  </div>
                  <div className="bg-white rounded-lg p-3 border border-gray-100">
                    <p className="text-gray-500 text-xs mb-1">Plan expires on</p>
                    <p className="font-bold text-gray-900">{planDetails?.expiry || 'N/A'}</p>
                  </div>
                </div>
              </div>

              <Button
                data-testid="payment-continue-btn"
                onClick={() => navigate(continueTo)}
                className="w-full bg-green-600 hover:bg-green-700 text-white py-3 text-base font-semibold mb-4"
              >
                {continueLabel}
              </Button>

              {isNewUser && (
                <p className="text-xs text-gray-400 mb-3">
                  We've also emailed you a secure access link in case you need to come back later.
                </p>
              )}

              <p className="text-xs text-gray-400 flex items-center gap-1">
                <Mail className="h-3 w-3" />
                Any concerns? Email us at{' '}
                <a href="mailto:info@crackcuet.com" className="text-blue-500 hover:underline ml-1">
                  info@crackcuet.com
                </a>
              </p>
            </div>
          )}

          {status === 'failed' && (
            <div className="flex flex-col items-center" data-testid="payment-status-failed">
              <div className="w-20 h-20 bg-red-100 rounded-full flex items-center justify-center mb-5">
                <XCircle className="h-12 w-12 text-red-500" />
              </div>
              <h2 className="text-xl font-bold text-gray-800 mb-2">Payment Verification Failed</h2>
              <p className="text-gray-500 mb-6">
                We couldn't verify your payment. If money was deducted, it will be refunded automatically.
              </p>
              <div className="flex gap-3 w-full mb-4">
                <Button variant="outline" onClick={() => navigate('/pricing')} className="flex-1">
                  Back to plans
                </Button>
                <Button onClick={() => navigate('/pricing')} className="flex-1">
                  Try Again
                </Button>
              </div>
              <p className="text-xs text-gray-400 flex items-center gap-1">
                <Mail className="h-3 w-3" />
                Need help? Contact us at{' '}
                <a href="mailto:info@crackcuet.com" className="text-blue-500 hover:underline ml-1">
                  info@crackcuet.com
                </a>
              </p>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
